import cx from 'classnames';
import _fromPairs from 'lodash/fromPairs';
import _snakeCase from 'lodash/snakeCase';
import { useMemo } from 'react';
import { ReactComponent as ExportIcon } from '../assets/icons/download.svg';
import AddToCartButton from './AddToCartButton';
import { useSaleTable } from './SaleTable';
import Tooltip from './Tooltip';
import ExportToCsvButton from './ExportToCsvButton';
import getSalePresenter from '../lib/getSalePresenter';

/**
 * @typedef Props
 * @prop {string} className
 * @prop {string=} csvDownloadUrl
 * @prop {boolean=} showAddToCart
 * @prop {import('./ExportToCsvButton').onGetCsvPage=} onGetCsvPage
 * @prop {string=} csvFilename
 */

/**
 * @param {Props} props
 */
export default function SaleTableActions({
  className = '',
  csvDownloadUrl = null,
  csvFilename = 'sales',
  onGetCsvPage,
  showAddToCart = false,
}) {
  const {
    flatRows,
    totalItems,
    visibleColumns,
    state: { filters, globalFilter },
  } = useSaleTable();

  const csvColumns = useMemo(
    () => [
      ...visibleColumns.filter(
        column => (column.canExport ?? true) && column.accessor,
      ),
    ],
    [visibleColumns],
  );

  return (
    <div
      className={cx(
        className,
        'tw-hidden md:tw-flex tw-flex-wrap tw-items-center md:tw-py-2 tw-text-sm tw-text-gray-700',
      )}>
      <div className="tw-mr-4 tw-h-12 tw-flex tw-flex-wrap tw-items-center tw-font-bold">
        {showAddToCart && (
          <AddToCartButton
            sales={flatRows
              .filter(
                row =>
                  row.original.saleType === 'buy' ||
                  row.original.selfHasActiveInvoice,
              )
              .map(row => row.original)}>
            {({ onClick, inCart }) => (
              <SaleTableActionButton
                disabled={inCart || flatRows.length === 0}
                disabledTooltipText="Please make a selection in order to add to cart"
                onClick={onClick}>
                <i className="gb-icon gb-icon-morpheus-cart-add tw-relative tw--top-1 tw-w-5 tw-h-5 tw-text-xl tw-mr-1" />
                Add all to cart
              </SaleTableActionButton>
            )}
          </AddToCartButton>
        )}
        {onGetCsvPage && (
          <div className="tw-hidden md:tw-flex">
            <ExportToCsvButton
              filename={csvFilename}
              onGetCsvPage={async (page, pageSize) => {
                const rows = await onGetCsvPage(page, pageSize);
                return rows
                  .map(row => getSalePresenter(row))
                  .map(row => ({
                    ..._fromPairs([
                      ...csvColumns.map(column => [
                        column.csvHeader || _snakeCase(column.id),
                        column.accessor(row),
                      ]),
                      [
                        'permalink',
                        row.saleType === 'buy'
                          ? `https://www.namecheap.com/market/buynow/${row.name}/`
                          : `https://www.namecheap.com/market/sale/${row.id}/`,
                      ],
                    ]),
                  }));
              }}
              rowCount={totalItems}>
              {({ onClick }) => (
                <SaleTableActionButton
                  disabled={flatRows.length === 0}
                  onClick={() => {
                    if (
                      csvDownloadUrl &&
                      filters.length === 0 &&
                      !globalFilter
                    ) {
                      window.open(csvDownloadUrl, '_blank');
                      return;
                    }
                    onClick();
                  }}>
                  <ExportIcon className="tw-w-5 tw-h-5 tw-mr-1 tw--mt-px" />
                  Download as CSV
                </SaleTableActionButton>
              )}
            </ExportToCsvButton>
          </div>
        )}
      </div>
    </div>
  );
}

const SaleTableActionButton = ({
  className = '',
  disabled,
  disabledTooltipText = '',
  ...props
}) => {
  const button = (
    <button
      className={cx(
        className,
        'tw-inline-flex tw-items-center tw-mx-2 focus:tw-outline-none focus-visible:tw-ring-2',
        {
          'hover:tw-text-secondary-dark': !disabled,
        },
      )}
      disabled={disabled}
      type="button"
      {...props}
    />
  );

  if (disabled && disabledTooltipText) {
    return (
      <Tooltip arrow enterDelay={1000} title={disabledTooltipText}>
        {button}
      </Tooltip>
    );
  }

  return button;
};
